import React, { useEffect, useState } from 'react';
import { Language, useConversation } from '@11labs/react';
import { useLocation } from 'react-router-dom';
import seekLogo from '../../assets/sai-seek-logo.png';
import './css/SaiElevenLabsCall.css';
import {
	getSaiCallHistory,
	getUserByUserId,
	updateUser,
} from '../../services/Firebase';
import lockIcon from '../../assets/new-icons/lock-icon.png';
import callIcon from '../../assets/new-icons/call-icon.png';
import endCallIcon from '../../assets/new-icons/end-call-icon.png';

type SaiCallHistoryItem = {
	conversationId: string;
	transcript_summary: string;
	agentId: string;
	emotionalStateRationale: string | null;
	main_language: string;
	conversation_id: string;
	keyProblemsRationale: string;
	call_duration_secs: number;
	finalEmotionalStateRationale: string | null;
	preferencesRationale: string;
	updatedAt: {
		_seconds: number;
		_nanoseconds: number;
	};
};

const SaiElevenLabsCallTest = () => {
	const { startSession, isSpeaking, status, endSession } = useConversation({
		preferHeadphonesForIosDevices: true,
		useWakeLock: false,
	});
	const [hasPermission, setHasPermission] = useState(false);
	const [timer, setTimer] = useState(0);
	const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(
		null
	);
	const [callHistory, setCallHistory] = useState<SaiCallHistoryItem[]>([]);
	const [language, setLanguage] = useState<Language>('en');
	const [firstMessage, setFirstMessage] = useState<string>('');
	const [overallUserInfo, setOverallUserInfo] = useState<string>('');
	const [userData, setUserData] = useState<any>();

	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const userId = searchParams.get('userId') || '';

	const requestMicrophonePermission = async () => {
		try {
			const permissions = await navigator.permissions.query({
				name: 'microphone' as PermissionName,
			});
			if (permissions.state === 'granted') {
				setHasPermission(true);
				return;
			}
			if (permissions.state !== 'denied') {
				await navigator.mediaDevices.getUserMedia({ audio: true });
				setHasPermission(true);
			} else {
				setHasPermission(false);
			}
		} catch (error) {
			setHasPermission(false);
		}
	};

	useEffect(() => {
		requestMicrophonePermission();
	}, []);

	useEffect(() => {
		const onMessage = (event: MessageEvent) => {
			const message = event.data;
			if (message === 'approved') {
				try {
					const dynamicVariables: any = { userId };

					dynamicVariables['overall_user_info'] = overallUserInfo;

					callHistory.forEach((call, index) => {
						dynamicVariables[`transcript_summary_${index + 1}`] =
							call.transcript_summary;
						// dynamicVariables[`main_language_${index + 1}`] = call.main_language;
						// dynamicVariables[`preferencesRationale_${index + 1}`] =
						// 	call.preferencesRationale;
						// dynamicVariables[`keyProblemsRationale_${index + 1}`] =
						// 	call.keyProblemsRationale;
						// dynamicVariables[`emotionalStateRationale_${index + 1}`] =
						// 	call.emotionalStateRationale;
						// dynamicVariables[`finalEmotionalStateRationale_${index + 1}`] =
						// 	call.finalEmotionalStateRationale;
						// dynamicVariables[`updatedAt_${index + 1}`] = String(
						// 	call.updatedAt._seconds
						// );
					});

					const overrides: any = {
						agent: { language, firstMessage },
					};

					startSession({
						agentId: 'T5Go4bkhSJSomx4pAKUG',
						dynamicVariables,
						overrides,
					}).then(() => {
						setTimer(0);
						const interval = setInterval(() => {
							setTimer((prev) => prev + 1);
						}, 1000);
						setTimerInterval(interval);
					});
				} catch (error) {
					console.error('Error starting conversation:', error);
				}
			}
		};
		window.addEventListener('message', onMessage);
		return () => {
			window.removeEventListener('message', onMessage);
		};
	}, [
		callHistory,
		firstMessage,
		language,
		overallUserInfo,
		startSession,
		userId,
	]);

	useEffect(() => {
		if (userId) {
			getUserByUserId(userId).then(({ userData }) => {
				setUserData(userData);
			});
			getSaiCallHistory(userId).then((response) => {
				if (response) {
					setCallHistory(response?.data);
					setFirstMessage(response?.first_message);
					setOverallUserInfo(response?.overall_user_info);
					setLanguage(
						response?.language || response?.data[0]?.main_language || 'en'
					);
				}
			});
		}
	}, [userId]);

	useEffect(() => {
		if (status === 'disconnected') {
			(window as any).ReactNativeWebView?.postMessage('disconnected-' + timer);
			if (timerInterval) {
				clearInterval(timerInterval);
			}
			setTimerInterval(null);
		}
	}, [status]);

	const handleStart = async () => {
		if (!hasPermission) {
			alert('Microphone permission denied');
			return;
		}

		if (status === 'connecting') {
			return;
		}

		(window as any).ReactNativeWebView?.postMessage('start');
	};

	const handleStop = () => {
		endSession();
		(window as any).ReactNativeWebView?.postMessage('end');

		if (timerInterval) {
			clearInterval(timerInterval);
		}
		setTimerInterval(null);
	};

	useEffect(() => {
		(window as any).ReactNativeWebView?.postMessage('timer-' + timer);
		if (timer === userData?.sai_call_seconds) {
			endSession();
			(window as any).ReactNativeWebView?.postMessage('limit reached');
			if (timerInterval) {
				clearInterval(timerInterval);
			}
			setTimerInterval(null);
		}
	}, [timer]);

	return (
		<div className='sai-container'>
			<div className='sai-bg-container'>
				{!!timerInterval && (
					<p className='sai-timer'>
						{String(Math.floor(timer / 60)).padStart(2, '0')}:
						{String(timer % 60).padStart(2, '0')}
					</p>
				)}
				{status === 'connecting' && <p className='sai-timer'>connecting...</p>}
				<div className='sai-animated-circle' />
				<img className='sai-logo' src={seekLogo} alt='' />
				<div className='sai-disclaimer'>
					<img className='sai-lock-icon' src={lockIcon} alt='' />
					<p>Conversations with Sai are 100% private and anonymous</p>
				</div>
				<div className='sai-button-container'>
					<div
						style={{
							background:
								status === 'connected'
									? '#eee'
									: 'linear-gradient(106deg, #4462ef 32.65%, #1b3dd8 105.18%)',
							color: status === 'connected' ? '#4a4a4a' : '#fff',
							pointerEvents:
								status === 'connecting' || !firstMessage ? 'none' : 'all',
						}}
						onClick={status === 'connected' ? handleStop : handleStart}
						className='sai-button'>
						<img src={status === 'connected' ? endCallIcon : callIcon} alt='' />
						{!firstMessage
							? 'Loading...'
							: status === 'connected'
							? 'End call'
							: 'Talk to Sai'}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SaiElevenLabsCallTest;
